import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import { useSearchParams } from "react-router-dom";
import ClientLayout from "../../components/portal/ClientLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Calendar,
  Filter,
  PieChart,
  Briefcase,
} from "lucide-react";

const ClientTimeTracking = () => {
  const { token, API } = useAuth();
  const [searchParams] = useSearchParams();
  const [timeData, setTimeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projectPhases, setProjectPhases] = useState({});
  const [selectedCategory, setSelectedCategory] = useState("all");
  
  // Project selection — default from URL query param
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(searchParams.get("project_id") || "all");
  const [loadingProjects, setLoadingProjects] = useState(true);

  useEffect(() => {
    fetchProjects();
    fetchProjectPhases();
  }, []);

  useEffect(() => {
    fetchTimeEntries(selectedCategory, selectedProject);
  }, [selectedCategory, selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${API}/client/projects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setProjects(data.projects || []);
      
      // If there's a URL project_id, keep it; otherwise use default
      const urlProjectId = searchParams.get("project_id");
      if (!urlProjectId && data.default_project_id) {
        setSelectedProject(data.default_project_id);
      }
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchProjectPhases = async () => {
    try {
      const response = await fetch(`${API}/client/project-phases`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setProjectPhases(data);
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const fetchTimeEntries = async (category = "all", projectId = "all") => {
    try {
      let url = `${API}/client/time-entries`;
      const params = new URLSearchParams();
      
      if (category && category !== "all") {
        params.append("category", category);
      }
      if (projectId && projectId !== "all") {
        params.append("project_id", projectId);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setTimeData(data);
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const hoursUsedPercent = timeData?.monthly_hours > 0
    ? (timeData.hours_used / timeData.monthly_hours) * 100
    : 0;

  const getCategoryColor = (category) => {
    const colors = {
      discovery: "bg-purple-100 text-purple-700",
      planning: "bg-blue-100 text-blue-700",
      implementation: "bg-green-100 text-green-700",
      training: "bg-yellow-100 text-yellow-700",
      review: "bg-orange-100 text-orange-700",
      compliance: "bg-red-100 text-red-700",
      support: "bg-teal-100 text-teal-700",
      documentation: "bg-indigo-100 text-indigo-700",
      meetings: "bg-pink-100 text-pink-700",
      research: "bg-cyan-100 text-cyan-700",
      other: "bg-gray-100 text-gray-700",
    };
    return colors[category] || colors.other;
  };

  return (
    <ClientLayout activeNav="time tracking">
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-xl font-bold text-rnb-navy">Time Tracking</h1>
            <p className="text-sm text-gray-400 mt-0.5">Track hours spent on your projects and monitor your monthly allocation.</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-rnb-teal/30 border-t-rnb-teal rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Project Selector */}
            {projects.length > 1 && (
              <Card data-testid="project-selector">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <Briefcase className="h-5 w-5 text-rnb-blue" />
                    <div className="flex-1">
                      <label className="text-sm font-medium text-rnb-navy mb-1 block">
                        Select Project
                      </label>
                      <Select value={selectedProject} onValueChange={setSelectedProject}>
                        <SelectTrigger className="w-full max-w-md" data-testid="project-filter">
                          <SelectValue placeholder="Select a project" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Projects</SelectItem>
                          {projects.map((project) => (
                            <SelectItem key={project.project_id} value={project.project_id}>
                              {project.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Hours Summary */}
            <Card data-testid="hours-summary">
              <CardContent className="p-8">
                <div className="grid md:grid-cols-3 gap-8">
                  <div className="text-center">
                    <p className="text-sm text-rnb-gray mb-2">Monthly Allocation</p>
                    <p className="font-mono-numbers text-4xl font-bold text-rnb-navy">
                      {timeData?.monthly_hours || 0}
                      <span className="text-lg text-rnb-gray ml-1">hrs</span>
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-rnb-gray mb-2">Hours Used</p>
                    <p className="font-mono-numbers text-4xl font-bold text-rnb-blue">
                      {timeData?.hours_used?.toFixed(1) || 0}
                      <span className="text-lg text-rnb-gray ml-1">hrs</span>
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-rnb-gray mb-2">Hours Remaining</p>
                    <p className={`font-mono-numbers text-4xl font-bold ${
                      timeData?.hours_remaining > 5 ? "text-rnb-teal" : "text-red-500"
                    }`}>
                      {timeData?.hours_remaining?.toFixed(1) || 0}
                      <span className="text-lg text-rnb-gray ml-1">hrs</span>
                    </p>
                  </div>
                </div>
                
                <div className="mt-8">
                  <div className="flex justify-between text-sm text-rnb-gray mb-2">
                    <span>Usage Progress</span>
                    <span>{hoursUsedPercent.toFixed(0)}%</span>
                  </div>
                  <Progress value={hoursUsedPercent} className="h-3" />
                </div>
              </CardContent>
            </Card>

            {/* Hours by Category */}
            {timeData?.hours_by_category && Object.keys(timeData.hours_by_category).length > 0 && (
              <Card data-testid="hours-by-category">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="h-5 w-5 text-rnb-blue" />
                    Hours by Category
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {Object.entries(timeData.hours_by_category).map(([category, hours]) => (
                      <div
                        key={category}
                        className="p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => setSelectedCategory(category)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getCategoryColor(category)}`}>
                            {projectPhases[category]?.name || category}
                          </span>
                        </div>
                        <p className="font-mono-numbers text-2xl font-bold text-rnb-navy">
                          {hours.toFixed(1)}
                          <span className="text-sm text-rnb-gray ml-1">hrs</span>
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Time Entries with Filter */}
            <Card data-testid="time-entries">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-rnb-blue" />
                    Time Entries
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-rnb-gray" />
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger className="w-48" data-testid="category-filter">
                        <SelectValue placeholder="Filter by category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {Object.entries(projectPhases).map(([key, phase]) => (
                          <SelectItem key={key} value={key}>{phase.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {timeData?.entries?.length > 0 ? (
                  <div className="space-y-4">
                    {timeData.entries.map((entry) => (
                      <div
                        key={entry.entry_id}
                        className="flex items-start justify-between p-4 bg-slate-50 rounded-lg"
                        data-testid={`time-entry-${entry.entry_id}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <span className="text-sm font-medium text-rnb-gray">
                              {formatDate(entry.date)}
                            </span>
                            {entry.category && (
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getCategoryColor(entry.category)}`}>
                                {entry.category_name || projectPhases[entry.category]?.name || entry.category}
                              </span>
                            )}
                          </div>
                          <p className="text-rnb-navy">{entry.description}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono-numbers text-xl font-bold text-rnb-blue">
                            {entry.hours}
                            <span className="text-sm text-rnb-gray ml-1">hrs</span>
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-rnb-gray py-8">
                    {selectedCategory !== "all" 
                      ? `No time entries for "${projectPhases[selectedCategory]?.name || selectedCategory}".`
                      : "No time entries recorded yet."}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </ClientLayout>
  );
};

export default ClientTimeTracking;
